import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { formatBRL } from "../data/chalets";
import { BookingDialog } from "./BookingDialog";

export const ChaletSection = ({ chalet, index }) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
    const [current, setCurrent] = useState(0);
    const [bookingOpen, setBookingOpen] = useState(false);
    const reversed = index % 2 === 1;

    const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
    const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => setCurrent(emblaApi.selectedScrollSnap());
        emblaApi.on("select", onSelect);
        return () => emblaApi.off("select", onSelect);
    }, [emblaApi]);

    return (
        <article
            id={chalet.id}
            data-testid={`chalet-section-${chalet.id}`}
            className={`py-16 lg:py-24 ${index % 2 === 1 ? "bg-[#F3F0E9]" : "bg-[#FDFBF7]"}`}
        >
            <div
                className={`max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                    reversed ? "lg:[&>*:first-child]:order-2" : ""
                }`}
            >
                <div className="relative group" data-testid={`gallery-${chalet.id}`}>
                    <div className="overflow-hidden rounded-sm border border-[#1B4332]/10" ref={emblaRef}>
                        <div className="flex">
                            {chalet.photos.map((src, i) => (
                                <div key={src} className="flex-[0_0_100%] min-w-0">
                                    <img
                                        src={src}
                                        alt={`${chalet.name} - foto ${i + 1}`}
                                        loading="lazy"
                                        className="w-full aspect-[4/3] object-cover"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    <button
                        data-testid={`gallery-prev-${chalet.id}`}
                        onClick={scrollPrev}
                        aria-label="Foto anterior"
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#FDFBF7]/90 backdrop-blur border border-[#1B4332]/10 flex items-center justify-center text-[#1B4332] hover:bg-[#1B4332] hover:text-white transition-colors duration-300 shadow-md"
                    >
                        <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                    <button
                        data-testid={`gallery-next-${chalet.id}`}
                        onClick={scrollNext}
                        aria-label="Próxima foto"
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#FDFBF7]/90 backdrop-blur border border-[#1B4332]/10 flex items-center justify-center text-[#1B4332] hover:bg-[#1B4332] hover:text-white transition-colors duration-300 shadow-md"
                    >
                        <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                    <div
                        data-testid={`gallery-counter-${chalet.id}`}
                        className="absolute bottom-4 right-4 bg-black/60 text-white text-xs font-bold px-3 py-1.5 rounded-full"
                    >
                        {current + 1} / {chalet.photos.length}
                    </div>
                </div>

                <div>
                    <p className="uppercase text-xs font-bold tracking-[0.2em] text-[#8B5A2B] mb-4">
                        Diária a partir de {formatBRL(chalet.price)}
                    </p>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#1B231A]">
                        {chalet.name}
                    </h2>
                    <p className="mt-3 text-base md:text-lg text-[#6C7A6B] font-normal">{chalet.short}</p>
                    <p className="mt-6 text-base leading-relaxed text-[#3E4C3D]">{chalet.description}</p>

                    <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3" data-testid={`amenities-${chalet.id}`}>
                        {chalet.amenities.map((item) => (
                            <li key={item} className="flex items-start gap-2.5 text-sm text-[#3E4C3D]">
                                <Check className="w-4 h-4 mt-0.5 text-[#1B4332] shrink-0" strokeWidth={2} />
                                {item}
                            </li>
                        ))}
                    </ul>

                    <div className="mt-10 flex items-center gap-6 flex-wrap">
                        <button
                            data-testid={`book-button-${chalet.id}`}
                            onClick={() => setBookingOpen(true)}
                            className="bg-[#1B4332] hover:bg-[#2D6A4F] text-white font-bold text-sm px-10 py-4 rounded-full transition-colors duration-300 shadow-md"
                        >
                            Reservar agora
                        </button>
                        <p className="text-sm text-[#6C7A6B]">
                            <span className="text-2xl font-bold text-[#1B231A]">{formatBRL(chalet.price)}</span> / noite
                        </p>
                    </div>
                </div>
            </div>

            <BookingDialog chalet={chalet} open={bookingOpen} onOpenChange={setBookingOpen} />
        </article>
    );
};
